/* eslint max-statements: ["error", {max: 25}] -- applies to entire file */
import { useEffect, useMemo, useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  MessagesAnalyticsEvent,
  MessagesAnalyticsKey,
  MessagesAnalyticsScreen,
} from "@/constants/analytics/Analytics";
import { ScreenPath, TabPath } from "@/constants/Navigation";
import { router } from "@/crossPlatform/expo-router";
import { useGroupChannel, useSendbirdChat } from "@/crossPlatform/Sendbird";
import useEncodedNavigation from "@/hooks/useEncodedNavigation";
import useEncodedSearchParams from "@/hooks/useEncodedSearchParams";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import ChannelDocumentList from "@/ui/components/messages/ChannelDocumentList";
import ChannelParticipantList from "@/ui/components/messages/ChannelParticipantList";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import { FAIconName, FullScreenSpinner, TabButton } from "@/ui/core";
import { HStack } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import { LayoutToken } from "@/ui/theme/tokens";
import { isIndividualChannel } from "@/utils/groupChannel.util";
import { sendbirdMemberToAvatarCard } from "@/utils/messages.util";
import { toAbsolutePath } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

type ChannelDetailTab = "files" | "participants";

const Modal = () => {
  const { channelDisplayName, channelUrl } = useEncodedSearchParams<{
    channelDisplayName: string;
    channelUrl: string;
  }>();

  const { sdk } = useSendbirdChat();
  const { captureMessage } = useAppMonitor();
  const { track, trackPageView } = useAnalytics();
  const { replaceWithEncoding } = useEncodedNavigation();

  const handleDismiss = () => {
    if (!isWeb()) {
      router.dismiss();
      return;
    }
    // on web, the parent group-channel may not be in the nav stack if the user landed here via refresh or direct URL
    replaceWithEncoding({
      params: { channelDisplayName, channelUrl },
      pathname: toAbsolutePath([TabPath.Messages, ScreenPath.GroupChannel]),
    });
  };

  const [selectedTab, setSelectedTab] =
    useState<ChannelDetailTab>("participants");

  useEffect(() => {
    if (isNil(channelDisplayName)) {
      captureMessage(`channelDisplayName is missing from ${channelUrl}`);
      return;
    }

    trackPageView(MessagesAnalyticsScreen.ChannelDetails, {
      [MessagesAnalyticsKey.ChannelName]: channelDisplayName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- due to captureMessage
  }, [channelDisplayName, trackPageView]);

  useEffect(() => {
    if (!isWeb() || !isNil(channelUrl)) return;
    captureMessage("channelUrl is missing from channel-details");
    router.dismiss();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: redirect on cold-load when params were never available (otherwise fires on tab-switch)
  }, []);

  if (!isWeb() && isNil(channelUrl)) {
    captureMessage("channelUrl is missing from channel-details");
    throw new Error("channelUrl is required");
  }

  const { channel } = useGroupChannel(sdk, channelUrl);
  const userAvatarList = useMemo(() => {
    return channel?.members.map((member) => sendbirdMemberToAvatarCard(member));
  }, [channel]);

  if (isNil(channel)) {
    return <FullScreenSpinner />;
  }

  const baseTestID = joinTestIDs(channelUrl, "channel-details");
  const isGroupChannel = !isIndividualChannel(channel.url);
  const headerIcon = isGroupChannel ? FAIconName.UserGroupSolid : undefined;
  const isParticipantTabActive = selectedTab === "participants";
  const isFilesTabActive = selectedTab === "files";

  const handleSelectedTab = (tab: ChannelDetailTab) => {
    let eventName: MessagesAnalyticsEvent | undefined;
    if (tab === "files") {
      eventName = MessagesAnalyticsEvent.FilesTabSelected;
    }

    if (tab === "participants") {
      eventName = MessagesAnalyticsEvent.ParticipantsTabSelected;
    }

    setSelectedTab(tab);

    if (!eventName || !channelDisplayName) {
      return;
    }

    track(eventName, {
      [AnalyticsFieldName.Source]: MessagesAnalyticsScreen.ChannelDetails,
      [MessagesAnalyticsKey.ChannelName]: channelDisplayName,
      [MessagesAnalyticsKey.IsGroupChannel]: isGroupChannel,
    });
  };

  return (
    <ModalLayout
      isListView
      renderHeader={() => {
        return (
          <ModalTitleHeader
            icon={headerIcon}
            onDismiss={handleDismiss}
            testID={baseTestID}
            title={channelDisplayName || channel.name}
          />
        );
      }}
    >
      <HStack
        paddingHorizontal={LayoutToken.ModalContentPaddingHorizontal}
        paddingVertical={16}
      >
        <TabButton
          accessibilityLabel="See channel participants"
          isActive={isParticipantTabActive}
          onPress={() => handleSelectedTab("participants")}
          testID={joinTestIDs(baseTestID, "participants")}
          title="Participants"
        />
        <TabButton
          accessibilityLabel="See channel files"
          isActive={selectedTab === "files"}
          onPress={() => handleSelectedTab("files")}
          testID={joinTestIDs(baseTestID, "files")}
          title="Files"
        />
      </HStack>
      {isFilesTabActive && (
        <ChannelDocumentList channel={channel} testID={baseTestID} />
      )}
      {isParticipantTabActive && (
        <ChannelParticipantList
          participants={userAvatarList}
          testID={baseTestID}
        />
      )}
    </ModalLayout>
  );
};

export default Modal;
